
import { DetailedSubscription, Subscription } from "@/types/Subscription";
import {
  getActiveSubscription,
  getActivePaidSubscription,
  getDetailedSubscriptions,
  getSubscriptions,
} from "@/lib/api/clients/SubscriptionClient";
import { getCookie } from "@/lib/auth/cookies";
import { ServerError } from "@/lib/api/errors/ErrorHandler";
import { logApiFailure } from "@/lib/api/errors/redactError";

export async function hasActiveSubscriptions(): Promise<boolean | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) throw new Error("validationError");

    return await getActiveSubscription(companyId);
  } catch (error) {
    return logApiFailure("subscription.fetchActive failed", error);
  }
}

export async function hasActivePaidSubscriptions(): Promise<boolean | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) throw new Error("validationError");

    return await getActivePaidSubscription(companyId);
  } catch (error) {
    return logApiFailure("subscription.fetchActivePaid failed", error);
  }
}

export async function fetchSubscriptions(
  fields: string,
): Promise<Subscription[] | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) throw new Error("validationError");

    const response = await getSubscriptions(companyId, fields);

    if (!response) return [];

    return response;
  } catch (error) {
    return logApiFailure("subscription.fetchAll failed", error);
  }
}

export async function fetchDetailedSubscriptions(
  fields: string,
): Promise<DetailedSubscription[] | ServerError> {
  try {
    const companyId = await getCookie("companyId");

    if (!companyId) throw new Error("validationError");

    const response = await getDetailedSubscriptions(
      companyId,
      fields,
    );

    if (!response) return [];

    return response;
  } catch (error) {
    const serverError = await logApiFailure("subscription.fetchDetailed failed", error);
    // Keep the HTTP status (Stripe go-live re-audit r4): the shared query
    // decides its retries by it, and a @RolesAllowed 403 has no body/code.
    const shaped = error as { status?: unknown; response?: { status?: unknown } };
    const status = shaped?.response?.status ?? shaped?.status;
    return typeof status === "number" ? { ...serverError, httpStatus: status } : serverError;
  }
}
